/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Info, RotateCcw } from 'lucide-react';
import { BmiCalc, BsaCalc } from './calculators/anthropometry';
import { EgfrCalc, Cha2ds2Calc, WellsPeCalc, Curb65Calc, PediatricDoseCalc } from './calculators/internalMedicine';
import { BradenCalc, BarthelCalc, News2Calc } from './calculators/nursing';

/**
 * Suite of bedside medical calculators. Pure client-side logic — zero LLM cost.
 * I singoli calcolatori vivono in `./calculators/*` (antropometria, medicina interna, infermieristica);
 * questo file contiene solo il registry e il layout a schede.
 */

// ---------------- Registry ----------------
const CALCULATORS = [
  { id: 'bmi', title: 'BMI', description: 'Indice di massa corporea', category: 'Antropometria', component: BmiCalc },
  { id: 'bsa', title: 'BSA (Mosteller)', description: 'Superficie corporea', category: 'Antropometria', component: BsaCalc },
  { id: 'egfr', title: 'eGFR (CKD-EPI 2021)', description: 'Funzione renale', category: 'Nefrologia', component: EgfrCalc },
  { id: 'cha2ds2', title: 'CHA₂DS₂-VASc', description: 'Rischio ictus in FA', category: 'Cardiologia', component: Cha2ds2Calc },
  { id: 'wells_pe', title: 'Wells score EP', description: 'Sospetto embolia polmonare', category: 'Cardio-respiratorio', component: WellsPeCalc },
  { id: 'curb65', title: 'CURB-65', description: 'Severita polmonite', category: 'Pneumologia', component: Curb65Calc },
  { id: 'ped_dose', title: 'Dose pediatrica', description: 'Calcolo mg/kg', category: 'Farmacologia', component: PediatricDoseCalc },
  { id: 'braden', title: 'Braden', description: 'Rischio ulcere da decubito', category: 'Infermieristica', component: BradenCalc },
  { id: 'barthel', title: 'Barthel Index', description: 'Autonomia funzionale (ADL)', category: 'Infermieristica', component: BarthelCalc },
  { id: 'news2', title: 'NEWS2', description: 'Early warning score', category: 'Infermieristica', component: News2Calc },
];

export const MedicalCalculators = () => {
  const [openId, setOpenId] = useState(CALCULATORS[0].id);
  const active = CALCULATORS.find((c) => c.id === openId) || CALCULATORS[0];
  const ActiveComp = active.component;
  return (
    <div className="grid gap-4 md:grid-cols-[240px_1fr]">
      <div className="space-y-1" data-testid="calculators-list">
        {CALCULATORS.map((c) => (
          <button
            key={c.id}
            onClick={() => setOpenId(c.id)}
            className={`w-full text-left rounded-xl border p-3 transition-colors ${openId === c.id ? 'bg-primary/10 border-primary' : 'bg-card border-border hover:border-primary/40'}`}
            data-testid={`calc-tab-${c.id}`}
          >
            <div className="font-semibold text-sm">{c.title}</div>
            <div className="text-[11px] text-muted-foreground">{c.description}</div>
            <Badge variant="secondary" className="mt-1 text-[9px] uppercase">{c.category}</Badge>
          </button>
        ))}
      </div>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="font-display">{active.title}</CardTitle>
              <CardDescription>{active.description}</CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setOpenId(active.id)} title="Reset">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ActiveComp key={openId} />
          <div className="mt-4 rounded-lg bg-muted/40 p-3 text-[11px] text-muted-foreground flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>Strumento di supporto clinico. Non sostituisce la valutazione medica ne le linee guida ufficiali.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MedicalCalculators;
